import Backbone from 'backbone'
import _ from 'lodash'
import mockSync from 'scoreboard/components/mock-sync'
import nestCollection from 'scoreboard/components/nest-collection'
import nestModel from 'scoreboard/components/nest-model'
import ChartDesignerFeature from './chart-designer-feature'
import ChartDesignerForecast from './chart-designer-forecast'
import ChartDesignerSeriePeriod from './chart-designer-serie-period'
import findByIdsFactory from './find-by-ids-factory'
import * as dataProperty from '../data-property'
import serieLabel from '../serie-label'


const findByBreakdownRangeIds = findByIdsFactory({
	idAttr: 'breakdownRangeId',
	tempIdAttr: 'breakdownRangeTempId'
})

/**
 * Model for a 'Serie' Feature
 * @extends ChartDesignerFeature
 */
const ChartDesignerSerie = ChartDesignerFeature.extend(/** @lends ChartDesignerSerie.prototype */ {
	initialize: function (attr, opt = {}) {
		ChartDesignerFeature.prototype.initialize.apply(this, arguments)

		this.periods = new PeriodCollection(this.get('periods'))
		nestCollection({
			parent: this,
			parentAttribute: 'periods',
			children: this.periods
		})

		this.forecastModel = new ChartDesignerForecast(this.get('forecastModel'))
		nestModel({
			parent: this,
			parentAttribute: 'forecastModel',
			child: this.forecastModel
		})
	},

	domainType: function () {
		return _.get(this.get('source'), 'domainType')
	},

	/**
	 * Returns the domain object ID (scorecardItemId, initiativeId, etc) for
	 * this serie. If this is a calculated serie and more than one domain object
	 * is associated wth it, null will be returned
	 */
	domainObjectId: function () {
		let source = this.get('source')

		switch (this.domainType()) {
			case 'scorecard-item': return source.scorecardItemId
			case 'initiative': return source.initiativeId
			case 'dataset': {
				let datasetIds = this.datasetIds()

				// #43390 A calculated serie may not have a startingDatasetId,
				// but if the aggregations in the equation only reference a
				// single dataset, we can use that.
				if (datasetIds.length === 1) {
					return datasetIds[0]
				} else {
					return null
				}
			}
		}
	},

	/**
	 * Returns all dataset IDs used in this serie. Considers startingDatasetId
	 * and any datasets that might be referenced in a calculated serie's equation
	 * @returns {Int[]}
	 */
	datasetIds: function () {
		if (this.domainType() !== 'dataset') {
			return []
		}

		let datasetIds = []
		let agg = this.get('source').aggregation
		if (agg.startingDatasetId) {
			datasetIds.push(agg.startingDatasetId)
		}

		// #43390 A calculated serie may not have a startingDatasetId,
		// but if the aggregations in the equation only reference a
		// single dataset, we can use that.
		 _(agg.aggregations)
			.map(agg => agg.datasetId)
			.compact()
			.uniq()
			.forEach(id => datasetIds.push(id))

		return datasetIds
	},

	isCalculated: function () {
		return _.get(this.get('source'), 'dataSourceType') === 'calculated'
	},

	hashCode: function() {
		return `${this.domainType()}_${this.domainObjectId()}`
	},

	getLabel: function({data = this.data}) {
		return serieLabel({ serie: this, domainData: data })
	},

	/**
	 * Converts serie.source to the generic format used by DataSourceSelector
	 *
	 * @return {StandardDataSource|DatasetAggregation}
	 */
	getGenericDataSource: function () {
		let serieSource = this.get('source')
		let domainType = serieSource.domainType
		let dataSource = domainType === 'dataset'
			? serieSource.aggregation
			: _.omit(serieSource, 'domainType')

		if (domainType === 'scorecard-item') {
			dataSource.domainObjectId = serieSource.scorecardItemId
			dataSource.dataProperty = dataProperty.joinDataPropertyOption(serieSource)
		} else if (domainType === 'initiative') {
			dataSource.domainObjectId = serieSource.initiativeId
		}

		return dataSource
	}
})

ChartDesignerSerie.TEMP_ID_PREFIX = 's'

const PeriodCollection = Backbone.Collection.extend({
	sync: mockSync,
	model: ChartDesignerSeriePeriod,

	/**
	 * Returns the serie period matching the given breakdown range's id/tempId
	 * @param {Integer} [id] if the range has been persisted, its ID
	 * @param {String} [tempId] if the range has not been persisted, its tempId
	 *
	 * @returns {ChartDesignerSeriePeriod} the period matching the given range,
	 * or null if none is found
	 */
	findByRangeIds: function ({ id, tempId }) {
		return findByBreakdownRangeIds({ id, tempId, collection: this })
	}
})

export default ChartDesignerSerie
export { PeriodCollection }

ChartDesignerSerie.__filename__ = 'charts/model/chart-designer-serie.js'
